/**
 * Shared inline loading indicator for panels, list containers, and page-level
 * suspense fallbacks. Replaces ~29 hand-rolled variants across the renderer
 * per MA-305 (2026-08-05). Style tokens follow docs/DESIGN-SYSTEM.md.
 *
 * Accessibility (verified against MDN aria-busy + aria-live docs 2026-08-05):
 *   - `role="status"` — implicit `aria-live="polite"`, so screen readers
 *     announce the label when it renders (matches "content updating" state).
 *   - `aria-busy="true"` — signals to assistive tech that this container is
 *     mid-update; announcements about content it will replace are deferred.
 *
 * Variants map to the three real-world use cases we saw before unification:
 *   - `inline`   — panel-level text-only placeholder (default; `text-xs muted`)
 *   - `padded`   — list-container placeholder that needs breathing room
 *     (dropdown lists, tree panels)
 *   - `centered` — page-level suspense fallback that fills its parent flex box
 */
import type { HTMLAttributes, JSX } from 'react';
import { cn } from './utils';

export type LoadingStateVariant = 'inline' | 'padded' | 'centered';

interface LoadingStateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Message shown next to the busy indicator. Default: "Loading…". */
  label?: string;
  /** Visual variant — inline, padded, or centered. Default: "inline". */
  variant?: LoadingStateVariant;
}

const VARIANT_CLASSES: Record<LoadingStateVariant, string> = {
  inline: 'text-xs text-muted-foreground',
  padded: 'px-3 py-2 text-xs text-muted-foreground',
  centered: 'flex h-full items-center justify-center text-sm text-muted-foreground',
};

export function LoadingState({
  label = 'Loading…',
  variant = 'inline',
  className,
  ...rest
}: LoadingStateProps): JSX.Element {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      data-testid="loading-state"
      className={cn(VARIANT_CLASSES[variant], className)}
      {...rest}
    >
      {label}
    </div>
  );
}
